// Trading-desk helpers shown alongside the analysis: position size for a chosen risk, and the
// part of the session the clock is in. Educational arithmetic, not advice.

export interface PositionSize {
  /** Whole shares whose loss at the stop is at most the chosen risk. */
  qty: number;
  /** Loss at the stop for qty shares. */
  atRisk: number;
  /** Cost of qty shares at the entry price. */
  value: number;
  /** The risk-based size cost more than the capital, so qty was cut to what the capital buys. */
  capped: boolean;
}

/** Shares to buy (or sell) so that a stop-out loses about riskPct% of capital. Null when the
 * inputs can't size a position (no capital, no risk per share, or not even one share fits). */
export function positionSize(capital: number, riskPct: number, entry: number, riskPerShare: number): PositionSize | null {
  if (!(capital > 0) || !(riskPct > 0) || !(entry > 0) || !(riskPerShare > 0)) return null;
  const byRisk = Math.floor((capital * riskPct) / 100 / riskPerShare);
  const byCash = Math.floor(capital / entry);
  const qty = Math.min(byRisk, byCash);
  if (qty < 1) return null;
  return { qty, atRisk: qty * riskPerShare, value: qty * entry, capped: byCash < byRisk };
}

export type SessionPhaseId = 'pre' | 'opening' | 'prime' | 'slow' | 'afternoon' | 'late' | 'auction' | 'closed';

export interface SessionPhase {
  id: SessionPhaseId;
  label: string;
  note: string;
}

const PHASES: Record<SessionPhaseId, Omit<SessionPhase, 'id'>> = {
  pre: { label: 'Before the open', note: 'The market opens at 09:15. Levels from yesterday are all there is so far.' },
  opening: { label: 'Opening noise', note: 'The first five minutes swing on overnight orders; most setups wait for the opening range to form.' },
  prime: { label: 'Prime time', note: 'The busiest, cleanest part of the day: moves tend to follow through.' },
  slow: { label: 'Midday slowdown', note: 'Volume thins out around lunch; breakouts fail more often and ranges chop.' },
  afternoon: { label: 'Afternoon trend', note: 'Volume returns and the day’s trend often resumes or reverses into the close.' },
  late: { label: 'Late session', note: 'Little time left for a new position to work; many traders only manage or close what they hold.' },
  auction: { label: 'Closing auction', note: 'F&O stocks stopped trading at 15:15 for the closing auction; other stocks trade until 15:30.' },
  closed: { label: 'Market closed', note: 'The session has ended; this is the full day as it finished.' },
};

/** The part of the NSE session at a clock minute (minutes since midnight, exchange time).
 * auction: F&O stocks move to the closing auction at 15:15 (from 3 Aug 2026). */
export function sessionPhase(minute: number, auction = false): SessionPhase {
  const id: SessionPhaseId =
    minute < 9 * 60 + 15 ? 'pre'
    : minute < 9 * 60 + 20 ? 'opening'
    : minute < 11 * 60 + 30 ? 'prime'
    : minute < 13 * 60 + 30 ? 'slow'
    : minute < 15 * 60 ? 'afternoon'
    : auction && minute >= 15 * 60 + 15 && minute < 15 * 60 + 30 ? 'auction'
    : minute < 15 * 60 + 30 ? 'late'
    : 'closed';
  return { id, ...PHASES[id] };
}

export interface Pivots { P: number; R1: number; R2: number; R3: number; S1: number; S2: number; S3: number }

/** Classic ("standard") floor-trader pivots from the previous session's high, low and close: the
 * R1–R3 / S1–S3 that Groww's terminal and TradingView's "Pivot Points Standard" show. */
export function classicPivots(high: number, low: number, close: number): Pivots | null {
  if (!(high > 0) || !(low > 0) || !(close > 0) || high < low) return null;
  const P = (high + low + close) / 3, range = high - low;
  return { P, R1: 2 * P - low, S1: 2 * P - high, R2: P + range, S2: P - range, R3: high + 2 * (P - low), S3: low - 2 * (high - P) };
}
